// Derives the mapping grid from the current selection: regenerates the rows when
// the integrations change, then exposes the pairs, per-source counters and the
// open/closed state of each collapsible section.

import { useCallback, useEffect, useMemo, useState } from 'react';
import partition from 'lodash/partition';

import {
  buildAutoMappings,
  groupPairsBySource,
  hasLocationAttributes,
  mappingStatsBySource,
  pairsOf,
} from './sharedSettingModel';

export default function useMappingEngine({
  selectedIntegrations,
  mappings,
  setMappings,
  savedIntegrationIds,
}) {
  // null until the user touches a section, so the first group can default to open.
  const [openGroupIds, setOpenGroupIds] = useState(null);

  // Skip when no integrations are selected — an empty list always produces empty
  // mappings, and running on mount would wipe any saved mappings that were
  // loaded by the edit effect before the async fetch completed.
  useEffect(() => {
    if (selectedIntegrations.length === 0) return;

    setMappings((previous) =>
      buildAutoMappings(selectedIntegrations, previous, savedIntegrationIds),
    );
  }, [selectedIntegrations, setMappings, savedIntegrationIds]);

  const [mappableIntegrations, lifecycleOnlyIntegrations] = useMemo(
    () => partition(selectedIntegrations, hasLocationAttributes),
    [selectedIntegrations],
  );

  // Every pair belongs to exactly one group and empty groups are dropped, so
  // pairGroups is also what tells the screen whether there is anything to map.
  const pairGroups = useMemo(
    () =>
      groupPairsBySource(mappableIntegrations, pairsOf(mappableIntegrations)),
    [mappableIntegrations],
  );

  const statsBySource = useMemo(
    () => mappingStatsBySource(mappings),
    [mappings],
  );

  const defaultOpenIds = useMemo(
    () => (pairGroups.length > 0 ? [pairGroups[0].integration.id] : []),
    [pairGroups],
  );

  const openIds = openGroupIds ?? defaultOpenIds;

  const allGroupsOpen =
    pairGroups.length > 0 &&
    pairGroups.every((group) => openIds.includes(group.integration.id));

  const toggleGroup = useCallback(
    (uuid) =>
      setOpenGroupIds((previous) => {
        const current = previous ?? defaultOpenIds;
        return current.includes(uuid)
          ? current.filter((id) => id !== uuid)
          : [...current, uuid];
      }),
    [defaultOpenIds],
  );

  const toggleAllGroups = useCallback(
    () =>
      setOpenGroupIds(
        allGroupsOpen ? [] : pairGroups.map((group) => group.integration.id),
      ),
    [allGroupsOpen, pairGroups],
  );

  const changeMapping = useCallback(
    (index, field, value) =>
      setMappings((previous) =>
        previous.map((m, i) => (i === index ? { ...m, [field]: value } : m)),
      ),
    [setMappings],
  );

  // The row index is what identifies a mapping, so it travels with the row.
  const mappingsForPair = useCallback(
    (fromUuid, toUuid) =>
      mappings
        .map((m, rowIdx) => ({ ...m, rowIdx }))
        .filter(
          (m) =>
            m.from_integration_uuid === fromUuid &&
            m.to_integration_uuid === toUuid,
        ),
    [mappings],
  );

  const mappedCount = mappings.filter((m) =>
    m.to_attribute_name?.trim(),
  ).length;
  const totalMappings = mappings.length;

  // Memoized so the mappings section, which takes the whole engine, only
  // re-renders when something it shows actually changed.
  return useMemo(
    () => ({
      lifecycleOnlyIntegrations,
      mappableIntegrations,
      pairGroups,
      statsBySource,
      openIds,
      allGroupsOpen,
      toggleGroup,
      toggleAllGroups,
      changeMapping,
      mappingsForPair,
      mappedCount,
      totalMappings,
      // A selection with no mappable pairs requires zero mappings, so it counts
      // as complete rather than as unfinished.
      mappingsComplete: mappedCount === totalMappings,
      mappedPct:
        totalMappings === 0
          ? 100
          : Math.round((mappedCount / totalMappings) * 100),
    }),
    [
      lifecycleOnlyIntegrations,
      mappableIntegrations,
      pairGroups,
      statsBySource,
      openIds,
      allGroupsOpen,
      toggleGroup,
      toggleAllGroups,
      changeMapping,
      mappingsForPair,
      mappedCount,
      totalMappings,
    ],
  );
}
